import { ThemeProvider } from '@mui/material/styles';
import firebase from 'firebase/compat/app';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Redirect,
    Route,
    BrowserRouter as Router,
    Switch,
    useLocation
} from 'react-router-dom';

import { FirebaseContext, FirebaseProvider } from './context/FirebaseContext';
import { UserContext, UserProvider } from './context/UserContext';
import JobPost from './pages/CreateJob/JobPost';
import JobQualifyRoutes from './pages/JobQualify/JobQualifyRoutes';
import JumpstartForm from './pages/Jumpstart/JumpstartForm';
import LoginRoutes from './pages/Login/LoginRoutes';
import { createDBUser } from './pages/Login/userManagement';
import BusinessProfile from './pages/Profile/business/BusinessProfile';
import CandidateProfile from './pages/Profile/candidate/CandidateProfile';
import CandidatesPublicPage from './public/pages/Candidates';
import EmployersPublicPage from './public/pages/Employers';
import PrivacyPolicyPage from './public/pages/PrivacyPolicy';
import ServicesAgreementPage from './public/pages/ServicesAgreement';
import TermsPage from './public/pages/Terms';
import redirects from './redirects';
import SecureSite from './SecureSite';
import theme from './Theme';

import type { User } from '../types/user';

function App({
    firebaseUser
}: {
    firebaseUser: firebase.User | null | undefined;
}) {
    const { user, setUser } = useContext(UserContext);
    const isBusiness = useMemo(
        () => (user.userID ? user.levylRole === 'business' : undefined),
        [user.levylRole, user.userID]
    );
    const { pathname } = useLocation();
    const pathUserType = useMemo(() => {
        if (pathname.includes('/signup/business')) return 'business';
        if (pathname.includes('/signup/user')) {
            return 'candidate';
        }
        return undefined;
    }, [pathname]);

    useEffect(() => {
        if (firebaseUser && pathUserType) {
            createDBUser(
                pathUserType,
                firebaseUser.email ?? '',
                firebaseUser.uid
            ).then((newUser: User) => {
                setUser(newUser);
            });
        }
    }, [firebaseUser, setUser, pathUserType]);

    return (
        <Switch>
            {redirects.map(({ path, to }) => (
                <Redirect key={path} from={path} to={to} />
            ))}
            <Route path='/public/career-seekers'>
                <CandidatesPublicPage />
            </Route>
            <Route path='/public/companies'>
                <EmployersPublicPage />
            </Route>
            <Route path='/public/terms'>
                <TermsPage />
            </Route>
            <Route path='/public/privacy-policy'>
                <PrivacyPolicyPage />
            </Route>
            <Route path='/public/services-agreement'>
                <ServicesAgreementPage />
            </Route>
            <Route path='/job/:jobId/qualify'>
                <JobQualifyRoutes />
            </Route>
            <Route path='*'>
                <Switch>
                    <Route path='/secure/:uid'>
                        {firebaseUser && <SecureSite />}
                        {firebaseUser === null && <Redirect to='/login' />}
                    </Route>
                    <Route
                        path={[
                            '/create/:uid/candidate',
                            '/edit/:uid/candidate'
                        ]}
                    >
                        {firebaseUser && isBusiness === false && (
                            <CandidateProfile />
                        )}
                        {(firebaseUser === null || isBusiness) && (
                            <Redirect to='/login' />
                        )}
                    </Route>
                    <Route path='/create/:uid/business/jumpstart' exact>
                        {firebaseUser && isBusiness && <JumpstartForm />}
                        {(firebaseUser === null || isBusiness === false) && (
                            <Redirect to='/login' />
                        )}
                    </Route>
                    <Route
                        path={[
                            '/edit/:uid/business/name',
                            '/edit/:uid/business/location'
                        ]}
                        exact
                    >
                        {firebaseUser && isBusiness && <BusinessProfile />}
                        {(firebaseUser === null || isBusiness === false) && (
                            <Redirect to='/login' />
                        )}
                    </Route>
                    <Route
                        path={[
                            '/create/:uid/business',
                            '/edit/:uid/business/:companyID'
                        ]}
                    >
                        {firebaseUser && isBusiness && <BusinessProfile />}
                        {(firebaseUser === null || isBusiness === false) && (
                            <Redirect to='/login' />
                        )}
                    </Route>
                    <Route
                        path={[
                            '/create/:uid/job/:companyID',
                            '/edit/:uid/job/:companyID/:jobID'
                        ]}
                    >
                        {firebaseUser && isBusiness && <JobPost />}
                        {(firebaseUser === null || isBusiness === false) && (
                            <Redirect to='/login' />
                        )}
                    </Route>
                    <Route
                        exact
                        path={[
                            '/login',
                            '/resetpassword',
                            '/signup/select',
                            '/signup/user',
                            '/signup/business',
                            '/signup/user/:uid',
                            '/signup/business/:uid'
                        ]}
                    >
                        <LoginRoutes user={firebaseUser} />
                    </Route>
                    <Redirect exact from={'/'} to={'/public/career-seekers'} />
                    <Redirect from={'*'} to={'/login'} />
                </Switch>
            </Route>
        </Switch>
    );
}

const AppWithContext = () => {
    const [firebaseUser, setFirebaseUser] = useState<
        firebase.User | null | undefined
    >(undefined);
    const { auth } = useContext(FirebaseContext);

    useEffect(() => {
        const unsubscribe = auth?.onAuthStateChanged((newUser) => {
            if (newUser) {
                setFirebaseUser(newUser);
            } else {
                setFirebaseUser(null);
            }
        });
        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [auth]);

    return (
        <UserProvider firebaseUser={firebaseUser as firebase.User}>
            <ThemeProvider theme={theme}>
                <App firebaseUser={firebaseUser} />
            </ThemeProvider>
        </UserProvider>
    );
};

const FirebaseApp = () => (
    <FirebaseProvider>
        <Router>
            <AppWithContext />
        </Router>
    </FirebaseProvider>
);

export default FirebaseApp;
